import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { AlertSeverity, FlightLeg } from '@/lib/schedule'
import {
  addLocalDays,
  clockAt,
  dayBandsFor,
  formatDuration,
  startOfLocalDay,
  ticksFor,
  timeRowHidden,
  type Clock,
  type SpanKey,
  type Window,
} from '@/lib/schedule/timeline'

/**
 * The board: tails down the side, time across the top.
 *
 * Four decisions carry it.
 *
 * **The scale is pixels per minute, fixed until the frame length changes.**
 * Picking a frame fits it to the width once; after that, opening a panel,
 * collapsing the navigation rail or resizing the window shows more or less of
 * the timeline instead of redrawing it at a new scale. A board that rescales
 * under the cursor loses the reader's place, and on this screen the reader is
 * usually mid-thought about one rotation.
 *
 * **The canvas is the loaded period, not a multiple of the frame.** Scrolling
 * sideways walks the whole schedule that has been imported — 1 to 30 September
 * and no further — so the board never offers days that were never loaded, and
 * never stops at an arbitrary boundary either. The frame is what the arrows
 * and the date field move; the scroll is free inside the period.
 *
 * **One scroll container, two sticky edges.** The axis sticks to the top and
 * the registration column to the left, so the two never drift out of step —
 * which is what happens as soon as they are two elements kept in sync by hand.
 *
 * **Only what is on screen is built.** Rows outside the viewport are a spacer,
 * and bars outside the scrolled window are not rendered at all: a month across
 * a hundred tails is 36.000 bars, and a screen holds a few hundred.
 */

const ROW_HEIGHT = 30
const BAND_HEIGHT = 28
const TICK_HEIGHT = 20

/** Registration column: full width, and a narrow one once the card is small. */
const LABEL_WIDTH = 148
const LABEL_WIDTH_NARROW = 92
const NARROW_CARD = 560

/** Closest two axis labels may sit, in pixels, before one of them is dropped. */
const LABEL_GAP = 52

export interface BoardRow {
  kind: 'tail' | 'group'
  key: string
  label: string
  /** Trio for a tail row, tail count for a group heading. */
  sub: string
  legs: FlightLeg[]
}

export function GanttBoard({
  rows,
  view,
  canvas,
  span,
  clock,
  nowUtc,
  clockLabel,
  selectedKey,
  severityOf,
  onSelect,
}: {
  rows: BoardRow[]
  /** Frame plus its run-up: what is fitted to the width and scrolled to. */
  view: Window
  /** Everything the board may be scrolled across — the loaded period. */
  canvas: Window
  span: SpanKey
  clock: Clock
  nowUtc: number
  /** Station code or UTC — what the axis is read in. */
  clockLabel: string
  selectedKey: string | null
  severityOf: (leg: FlightLeg) => AlertSeverity | null
  onSelect: (leg: FlightLeg) => void
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const placed = useRef('')
  const scrolled = useRef('')
  const [scroll, setScroll] = useState({ x: 0, y: 0 })
  const [viewport, setViewport] = useState({ width: 0, height: 0 })
  const [pxPerMinute, setPxPerMinute] = useState(0)

  const labelWidth = viewport.width > 0 && viewport.width < NARROW_CARD ? LABEL_WIDTH_NARROW : LABEL_WIDTH
  const laneViewport = Math.max(0, viewport.width - labelWidth)

  const viewMinutes = (view.endUtc - view.startUtc) / 60_000
  const laneWidth = ((canvas.endUtc - canvas.startUtc) / 60_000) * pxPerMinute
  const xOf = useCallback(
    (utcMs: number) => ((utcMs - canvas.startUtc) / 60_000) * pxPerMinute,
    [canvas.startUtc, pxPerMinute],
  )

  useLayoutEffect(() => {
    const scroller = scrollRef.current
    if (!scroller) return
    const measure = () =>
      setViewport({ width: scroller.clientWidth, height: scroller.clientHeight })
    const observer = new ResizeObserver(measure)
    observer.observe(scroller)
    measure()
    return () => observer.disconnect()
  }, [])

  // The scale is set by a deliberate move — a new frame length, a new date,
  // the arrows, Now — and by nothing else. Opening a leg, collapsing the
  // navigation rail and resizing the window all leave it alone, so the board
  // shows more or less of the timeline instead of redrawing at a new scale.
  const key = `${span}|${view.startUtc}`
  useLayoutEffect(() => {
    if (laneViewport <= 0 || viewMinutes <= 0) return
    if (placed.current === key && pxPerMinute > 0) return
    placed.current = key
    setPxPerMinute(laneViewport / viewMinutes)
  }, [key, laneViewport, viewMinutes, pxPerMinute])

  // ...and the same move puts the frame back under the viewport. Scrolling by
  // hand afterwards is left alone.
  useLayoutEffect(() => {
    const scroller = scrollRef.current
    if (!scroller || pxPerMinute <= 0) return
    // The scale is part of the key: a move that also refits arrives in two
    // passes, and scrolling only on the first would place the frame using the
    // scale it is about to stop having.
    const settled = `${placed.current}|${pxPerMinute}`
    if (scrolled.current === settled) return
    scrolled.current = settled
    scroller.scrollLeft = xOf(view.startUtc)
  }, [pxPerMinute, view.startUtc, xOf])

  // Scroll fires far faster than the board can usefully redraw; one frame of
  // coalescing keeps a 12.000-leg month smooth.
  const pending = useRef(false)
  const onScroll = useCallback(() => {
    if (pending.current) return
    pending.current = true
    requestAnimationFrame(() => {
      pending.current = false
      const scroller = scrollRef.current
      if (scroller) setScroll({ x: scroller.scrollLeft, y: scroller.scrollTop })
    })
  }, [])

  // What is on screen, plus one screen on each side. Everything below is drawn
  // for this window rather than for the whole period: a month of half-hour
  // ticks is 1.440 lines, and three of them would be a browser's afternoon.
  const minutesOnScreen = pxPerMinute > 0 ? laneViewport / pxPerMinute : viewMinutes
  const drawFrom = Math.max(
    canvas.startUtc,
    canvas.startUtc + (scroll.x / (pxPerMinute || 1) - minutesOnScreen) * 60_000,
  )
  const drawTo = Math.min(canvas.endUtc, drawFrom + minutesOnScreen * 3 * 60_000)

  const ticks = useMemo(
    () => ticksFor({ startUtc: drawFrom, endUtc: drawTo }, span, clock),
    [drawFrom, drawTo, span, clock],
  )
  // Bands run whole local days, so a day keeps its own width and its label
  // stays centred on it however the window happens to be scrolled.
  const bands = useMemo(
    () =>
      dayBandsFor(
        {
          startUtc: startOfLocalDay(drawFrom, clock),
          endUtc: addLocalDays(startOfLocalDay(drawTo, clock), 1, clock),
        },
        clock,
      ),
    [drawFrom, drawTo, clock],
  )
  const headerHeight = BAND_HEIGHT + (timeRowHidden(span) ? 0 : TICK_HEIGHT)

  // Labels are thinned to what the lane can hold: at phone width a three-hour
  // grid would print its hours on top of each other.
  const labelled = useMemo(() => {
    const out: typeof ticks = []
    let lastX = -Infinity
    for (const tick of ticks) {
      if (!tick.major || tick.dayStart) continue
      const x = ((tick.utcMs - canvas.startUtc) / 60_000) * pxPerMinute
      if (x - lastX < LABEL_GAP) continue
      out.push(tick)
      lastX = x
    }
    return out
  }, [ticks, canvas.startUtc, pxPerMinute])

  const overscan = 6
  const firstRow = Math.max(0, Math.floor(scroll.y / ROW_HEIGHT) - overscan)
  const lastRow = Math.min(
    rows.length,
    Math.ceil((scroll.y + viewport.height) / ROW_HEIGHT) + overscan,
  )
  const visibleRows = rows.slice(firstRow, lastRow)

  const nowX = nowUtc >= canvas.startUtc && nowUtc < canvas.endUtc ? xOf(nowUtc) : null
  const ready = pxPerMinute > 0

  return (
    <div className="overflow-hidden rounded-[24px] border border-hairline bg-canvas">
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="h-[clamp(320px,calc(100vh-420px),900px)] overflow-auto"
      >
        <div style={{ width: ready ? labelWidth + laneWidth : '100%' }}>
          <div
            className="sticky top-0 z-30 flex border-b border-hairline bg-canvas"
            style={{ height: headerHeight }}
          >
            <div
              className="sticky left-0 z-10 flex shrink-0 items-end border-r border-hairline bg-canvas px-3 pb-1.5"
              style={{ width: labelWidth }}
            >
              <span className="t-caption-strong text-muted">{clockLabel}</span>
            </div>

            <div className="relative shrink-0" style={{ width: laneWidth }}>
              <div className="relative" style={{ height: BAND_HEIGHT }}>
                {bands.map((band) => {
                  const left = xOf(band.start)
                  const width = xOf(band.end) - left
                  return (
                    <div
                      key={band.key}
                      className="absolute top-0 flex items-center justify-center overflow-hidden
                                 border-l border-hairline"
                      style={{ left, width, height: BAND_HEIGHT }}
                    >
                      <span className="t-caption-strong truncate px-1 text-ink">
                        {width > 96 ? band.label : band.label.slice(4, 6)}
                      </span>
                    </div>
                  )
                })}
              </div>

              {!timeRowHidden(span) && (
                <div className="relative" style={{ height: TICK_HEIGHT }}>
                  {labelled.map((tick) => (
                    <span
                      key={tick.utcMs}
                      className="t-caption absolute -translate-x-1/2 font-mono text-muted"
                      style={{ left: xOf(tick.utcMs) }}
                    >
                      {clockAt(tick.utcMs, clock)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="relative">
            {/* One grid for every row, drawn once rather than per row. */}
            <div
              className="pointer-events-none absolute top-0 bottom-0 z-0"
              style={{ left: labelWidth, width: laneWidth }}
            >
              {ticks.map((tick) => (
                <div
                  key={tick.utcMs}
                  className={`absolute inset-y-0 w-px ${
                    tick.dayStart
                      ? 'bg-hairline'
                      : tick.major
                        ? 'bg-hairline-soft'
                        : 'bg-hairline-soft/60'
                  }`}
                  style={{ left: xOf(tick.utcMs) }}
                />
              ))}
              {nowX !== null && (
                <div className="absolute inset-y-0 w-px bg-primary" style={{ left: nowX }} />
              )}
            </div>

            <div style={{ height: firstRow * ROW_HEIGHT }} />

            {visibleRows.map((row) =>
              row.kind === 'group' ? (
                <div
                  key={row.key}
                  className="flex border-b border-hairline bg-surface-soft"
                  style={{ height: ROW_HEIGHT }}
                >
                  <div
                    className="sticky left-0 z-20 flex shrink-0 items-center gap-2 border-r
                               border-hairline bg-surface-soft px-3"
                    style={{ width: labelWidth }}
                  >
                    <span className="t-caption-strong truncate text-ink">{row.label}</span>
                  </div>
                  <div className="flex shrink-0 items-center px-3" style={{ width: laneWidth }}>
                    <span className="t-caption text-muted">{row.sub}</span>
                  </div>
                </div>
              ) : (
                <div
                  key={row.key}
                  className="flex border-b border-hairline-soft"
                  style={{ height: ROW_HEIGHT }}
                >
                  <div
                    className="sticky left-0 z-20 flex shrink-0 items-center gap-1.5 border-r
                               border-hairline bg-canvas px-3"
                    style={{ width: labelWidth }}
                  >
                    <span className="t-number-sm shrink-0 text-ink">{row.label}</span>
                    {/* A truncated aircraft type says less than no type at all. */}
                    {row.sub && labelWidth === LABEL_WIDTH && (
                      <span className="t-caption truncate text-muted-soft">{row.sub}</span>
                    )}
                  </div>

                  <div className="relative shrink-0" style={{ width: laneWidth }}>
                    {row.legs.map((leg) =>
                      leg.staUtc <= drawFrom || leg.stdUtc >= drawTo ? null : (
                        <Bar
                          key={leg.key}
                          leg={leg}
                          clock={clock}
                          xOf={xOf}
                          laneWidth={laneWidth}
                          selected={leg.key === selectedKey}
                          severity={severityOf(leg)}
                          onSelect={onSelect}
                        />
                      ),
                    )}
                  </div>
                </div>
              ),
            )}

            <div style={{ height: Math.max(0, (rows.length - lastRow) * ROW_HEIGHT) }} />
          </div>
        </div>
      </div>
    </div>
  )
}

function Bar({
  leg,
  clock,
  xOf,
  laneWidth,
  selected,
  severity,
  onSelect,
}: {
  leg: FlightLeg
  clock: Clock
  xOf: (utcMs: number) => number
  laneWidth: number
  selected: boolean
  severity: AlertSeverity | null
  onSelect: (leg: FlightLeg) => void
}) {
  // Clipped at the edges of the canvas rather than drawn past them: a leg that
  // runs off the end would otherwise widen the scroll area by its own length.
  // The clipped side loses its rounded corner, so a cut bar never reads as a
  // short flight.
  const from = xOf(leg.stdUtc)
  const to = xOf(leg.staUtc)
  const left = Math.max(0, from)
  const width = Math.max(Math.min(laneWidth, to) - left, 3)
  const clip = `${from < 0 ? 'rounded-l-none ' : ''}${to > laneWidth ? 'rounded-r-none' : ''}`

  const tone = leg.cancelledAt
    ? 'border-hairline bg-canvas text-muted-soft line-through'
    : severity === 'blocking'
      ? 'border-semantic-down/45 bg-semantic-down/8 text-semantic-down'
      : severity === 'warning'
        ? 'border-accent-yellow/55 bg-accent-yellow/10 text-ink'
        : leg.serviceType && leg.serviceType !== 'J'
          ? 'border-dashed border-hairline bg-canvas text-body'
          : 'border-hairline bg-surface-strong text-ink'

  const actual = actualSpan(leg)

  return (
    <>
      {actual && (
        <span
          aria-hidden="true"
          className="absolute bottom-[2px] h-[2px] rounded-full bg-ink/40"
          style={{
            left: Math.max(0, xOf(actual[0])),
            width: Math.max(
              Math.min(laneWidth, xOf(actual[1])) - Math.max(0, xOf(actual[0])),
              3,
            ),
          }}
        />
      )}
      <button
        type="button"
        onClick={() => onSelect(leg)}
        title={legTitle(leg, clock)}
        className={`absolute top-[4px] flex h-[22px] items-center overflow-hidden rounded-[6px]
                    border px-1 text-left transition-colors ${tone} ${clip}
                    ${selected ? 'border-primary ring-1 ring-primary' : ''}`}
        style={{ left, width }}
      >
        {width > 56 && (
          <span className="t-caption-strong truncate">
            {width > 104 ? `${leg.flightNumber} ` : ''}
            {leg.dep}-{leg.arr}
          </span>
        )}
      </button>
    </>
  )
}

/**
 * What the leg actually did, under what it was scheduled to do.
 *
 * A thin line rather than a second bar, and in ink rather than in red: the
 * shift reads a delay from the offset between the two, and DESIGN.md keeps the
 * semantic colours for text. A leg that ran to schedule draws nothing.
 */
function actualSpan(leg: FlightLeg): [number, number] | null {
  if (leg.cancelledAt) return null
  const from = leg.etdUtc ?? leg.stdUtc
  const to = leg.etaUtc ?? leg.staUtc
  if (from === leg.stdUtc && to === leg.staUtc) return null
  return [Math.min(from, to), Math.max(from, to)]
}

/**
 * Native tooltip: the whole leg in one line, without a second render tree.
 *
 * Station local first, because that is what the file states and what the shift
 * says out loud — "nine o'clock out of Saigon". The axis clock follows in
 * brackets so the bar's position on the grid is accounted for too.
 */
function legTitle(leg: FlightLeg, clock: Clock): string {
  const zone = clock.mode === 'utc' ? 'UTC' : 'LT'
  const parts = [
    `${leg.flightNumber} ${leg.dep}-${leg.arr}`,
    `${leg.dep} ${leg.stdLocal} → ${leg.arr} ${leg.staLocal} local`,
    `${clockAt(leg.stdUtc, clock)}-${clockAt(leg.staUtc, clock)} ${zone}`,
    `blk ${formatDuration(leg.blockMinutes)}`,
  ]
  if (leg.regn) parts.push(leg.regn)
  if (leg.etdUtc !== null) {
    const delay = Math.round((leg.etdUtc - leg.stdUtc) / 60_000)
    if (delay !== 0) parts.push(`ETD ${leg.etdLocal} (${delay > 0 ? '+' : ''}${delay})`)
  }
  if (leg.cancelledAt) parts.push('cancelled')
  return parts.join(' · ')
}
